import momentTimezone from 'moment-timezone';

import { moment } from '@eb/date';
import { constants } from '@eb/datetime';
import { getWindowObject } from '@eb/feature-detection';

import {
    DISPLAY_SALES_STATUS_CUSTOM,
    DISPLAY_SALES_STATUS_NOT_YET_ON_SALE,
    DISPLAY_SALES_STATUS_NOT_YET_ON_SALE_ACCESS_CODED,
    DISPLAY_SALES_STATUS_ON_SALE,
    DISPLAY_SALES_STATUS_SALES_ENDED,
    EVENT_STATUS_BUTTON_CANCELLED,
    EVENT_STATUS_BUTTON_ENDED,
    EVENT_STATUS_BUTTON_PUBLISHED,
    EVENT_STATUS_BUTTON_PUBLISH_SCHEDULED,
    EVENT_STATUS_BUTTON_UNPUBLISHED,
    EVENT_STATUS_DRAFT,
    EVENT_TEXT_CODE_TICKETS_NOT_YET_ON_SALE_KEY,
    EVENT_TEXT_CODE_TICKETS_SOLD_OUT_KEY,
    EVENT_TEXT_CODE_TICKETS_WITH_SALES_ENDED_KEY,
} from '../constants';

import {
    EVENT_START_PUBLISH_DATE,
    EVENT_START_PUBLISH_TIME,
} from '../validation';

const { ISO_8601_FORMAT, ISO_8601_DATE_FORMAT } = constants;

export const PUBLISHED_STATUS_LIST = ['live', 'started', 'ended', 'completed'];

const PUBLISHABLE_STATUSES = ['unsaved', 'draft', 'canceled'];

const UNPUBLISHABLE_STATUSES = ['live', 'started', 'ended', 'completed'];

export const API_STATUS_CANCELED = 'canceled';
export const API_STATUS_ENDED = 'ended';
export const API_STATUS_COMPLETED = 'completed';

const REQUIRED_EVENT_PROPS_TO_PUBLISH = ['name', 'start', 'end', 'organizerId'];

const PUBLISH_SCHEDULED_STATUS = 'scheduled';

/**
 * Whether the event is publishable in its current form
 *
 * @return boolean
 */
export const eventIsPublishable = ({ status, ...eventStatusData }) =>
    PUBLISHABLE_STATUSES.includes(status) &&
    REQUIRED_EVENT_PROPS_TO_PUBLISH.every(
        (eventProp) => eventStatusData[eventProp],
    );

/**
 * Whether the event is published and can be unpublished
 *
 * @return bool
 */
export const eventIsUnpublishable = ({ status }) =>
    UNPUBLISHABLE_STATUSES.includes(status);

/**
 * Whether the event is canceled.
 *
 * @return bool
 */
export const eventIsCanceled = ({ status }) => status === API_STATUS_CANCELED;

/**
 * Check if the event is ended or completed.
 * 'ended' status shows that the event date has passed and 'completed' that the event payouts were issued

 * @return bool
 */
export const eventIsEnded = ({ status }) =>
    status === API_STATUS_ENDED || status === API_STATUS_COMPLETED;

/**
 * eventEndDateHasPassed
 *
 * Determine if the event's end date has passed the today's date and time.
 *
 * @param [object], event data object
 * @return [bool], True, if event's end time is in the past.
 * @deprecated
 * TODO:see it this is needed. 'ended' status shows that the event date has passed and 'completed' that the event payouts were issued
 *
 */
export const eventEndDateHasPassed = (endTime, timeZone) => {
    const currentTime = momentTimezone().tz(timeZone);
    const eventEndTime = momentTimezone.tz(endTime, ISO_8601_FORMAT, timeZone);

    return eventEndTime.isBefore(currentTime);
};

/**
 * eventPublished
 * Checks if an event is published.
 *
 * @param {string} eventStatus - Status of the event
 * @return {boolean} True, if the event is published. False, otherwise.
 */
export const eventPublished = (eventStatus) =>
    PUBLISHED_STATUS_LIST.includes(eventStatus);

/**
 * eventIsScheduledToBePublished
 * Checks if an event is scheduled to be published.
 *
 * @param {string} scheduleStatus - Publish status of the event
 * @return {boolean} True, if the event is scheduled to be published. False, otherwise.
 */
export const eventIsScheduledToBePublished = (scheduleStatus) =>
    scheduleStatus === PUBLISH_SCHEDULED_STATUS;

/**
 * getStatusErrorStringFromTranslatedError
 * Accepts an error generated by using translateServerErrors and simplifies it to a string.
 *
 * @param {SubmissionError} error - a specific error generated by translateServerErrors
 */
export const getErrorStringFromTranslatedError = (error, defaultErrorMessage) =>
    (error && error.errors && error.errors._error) || defaultErrorMessage;

/**
 * eventIsFree
 * Event is free if is_free flag is true, or assumed to be free when there is no eventData.
 *
 * @param [object], event data object
 * @return [bool], True if event has no paid tickets
 */
export const eventIsFree = (event) => !event || (event && !!event['is_free']);

/**
 * eventIsSeriesParent
 * Event is series parent is `is_series_parent` is true
 *
 * @param [object], event data object
 * @return [bool], True if event is series parent.
 */
export const eventIsSeriesParent = (event) =>
    event && !!event['is_series_parent'];

/**
 * eventIsSeriesChild
 * Event is series child is `is_series` is true and `is_series_parent` is false
 *
 * @param [object], event data object
 * @return [bool], True if event is series child.
 */
export const eventIsSeriesChild = (event) =>
    event && !!event['is_series'] && !event['is_series_parent'];

/**
 * eventIsOnline
 * Event is online event is `online_event` is true
 *
 * @param [object], event data object
 * @return [bool], True if event is online.
 */
export const eventIsOnline = (event) => event && !!event['online_event'];

/**
 * gtmTrackingPixelEvent
 *
 * Google Tag Manager (GTM) is a product we use to embed Tracking Pixels for
 * certain analytics.  This function adds a new "tracking pixel" "event" if
 * applicable.
 *
 * @param [string] event to track
 * @return undefined
 */
export const gtmTrackingPixelEvent = (event) => {
    const dataLayer = getWindowObject('dataLayer') || [];

    dataLayer.push({ event });
};

/**
 * formatSalesStatusData
 * Returns an object with the formatted data to save the sales status.
 *
 * @return [object], formatted data
 */
export const formatSalesStatusData = ({
    statusCode,
    customMessage,
    isNotYetOnSale,
    isSoldOut,
}) => {
    // By default we override the sales ended message
    let eventTextKey = EVENT_TEXT_CODE_TICKETS_WITH_SALES_ENDED_KEY;
    const isCustomMessage = statusCode === DISPLAY_SALES_STATUS_CUSTOM;

    // Next we check for particular cases
    if (isSoldOut) {
        eventTextKey = EVENT_TEXT_CODE_TICKETS_SOLD_OUT_KEY;
    } else if (isNotYetOnSale && isCustomMessage) {
        eventTextKey = EVENT_TEXT_CODE_TICKETS_NOT_YET_ON_SALE_KEY;
    }

    const formattedData = {
        [eventTextKey]: {},
    };

    // If the status code is custom, we set the message.
    // Otherwise we set the message_code.
    if (isCustomMessage) {
        formattedData[eventTextKey].message = customMessage;
    } else {
        formattedData[eventTextKey]['message_code'] = statusCode;
    }

    return {
        texts: formattedData,
    };
};

export const getRedirectURL = (eventId) =>
    `/myevent/${eventId}/invite-and-promote/?showSuccessAlert=1`;

export const getEventStatusButtonType = (eventData) => {
    let eventStatus = null;
    if (!eventData) {
        return null;
    }
    const validations = [
        {
            value: EVENT_STATUS_BUTTON_CANCELLED,
            condition: eventIsCanceled(eventData),
        },
        {
            value: EVENT_STATUS_BUTTON_ENDED,
            condition: eventIsEnded(eventData),
        },
        {
            value: EVENT_STATUS_BUTTON_PUBLISHED,
            condition: eventIsUnpublishable(eventData),
        },
        {
            value: EVENT_STATUS_BUTTON_PUBLISH_SCHEDULED,
            condition:
                eventData.status === EVENT_STATUS_DRAFT &&
                eventData.hasSchedulePublish,
        },
        {
            value: EVENT_STATUS_BUTTON_UNPUBLISHED,
            condition: eventIsPublishable(eventData),
        },
    ];

    validations.some((item) => {
        if (item.condition && !eventStatus) {
            return (eventStatus = item.value);
        }
    });

    return eventStatus;
};

export const isStatusCustom = ({
    salesStatusMessageCode,
    salesStatusMessageType,
}) =>
    !salesStatusMessageCode &&
    salesStatusMessageType === DISPLAY_SALES_STATUS_CUSTOM;

export const isStatusOnSale = ({ isOnSale, salesStatusCode }) =>
    isOnSale && salesStatusCode === DISPLAY_SALES_STATUS_ON_SALE;

export const isStatusNotYetOnSale = ({ isNotYetOnSale, salesStatusCode }) =>
    isNotYetOnSale && salesStatusCode === DISPLAY_SALES_STATUS_NOT_YET_ON_SALE;

export const isStatusNotYetOnSaleAccessCoded = ({
    isNotYetOnSaleAccessCoded,
    salesStatusCode,
}) =>
    isNotYetOnSaleAccessCoded &&
    salesStatusCode === DISPLAY_SALES_STATUS_NOT_YET_ON_SALE_ACCESS_CODED;

export const isStatusSalesEnded = ({
    salesStatusMessageCode,
    salesStatusCode,
}) =>
    salesStatusMessageCode === EVENT_TEXT_CODE_TICKETS_WITH_SALES_ENDED_KEY &&
    salesStatusCode === DISPLAY_SALES_STATUS_SALES_ENDED;

export const getStatusCode = (eventSalesStatus = {}) => {
    const {
        salesStatusMessageCode,
        salesStatusMessageType,
        salesStatusCode,
    } = eventSalesStatus;
    let statusCode = salesStatusMessageCode;

    if (isStatusCustom(eventSalesStatus)) {
        statusCode = salesStatusMessageType;
    } else if (
        isStatusOnSale(eventSalesStatus) ||
        isStatusNotYetOnSale(eventSalesStatus) ||
        isStatusSalesEnded(eventSalesStatus)
    ) {
        statusCode = salesStatusCode;
    }

    return statusCode;
};

export const getTimeForTimePicker = (dateTimeString) => {
    const separatedTimeFromDate = dateTimeString.split('T')[1];

    return moment(separatedTimeFromDate.split('.')[0], 'HH:mm').format(
        'h:mm A',
    );
};

export const getDefaultValuesSchedulePublishFormData = (
    schedulePublishDateTime,
    eventTimezone,
) => {
    const defaultDate = momentTimezone().tz(eventTimezone).add(30, 'minutes');
    const remainder = 30 - (defaultDate.minute() % 30);
    const defaultTime = moment(defaultDate).add(remainder, 'minutes');
    let schedulePublishDateMoment;

    if (schedulePublishDateTime) {
        schedulePublishDateMoment = moment(schedulePublishDateTime).tz(
            eventTimezone,
        );
    }

    const defaultDateShown = schedulePublishDateMoment || defaultDate;
    const defaultTimeShown = schedulePublishDateMoment || defaultTime;
    const initForm = {
        [EVENT_START_PUBLISH_DATE]: defaultDateShown.format(
            ISO_8601_DATE_FORMAT,
        ),
        [EVENT_START_PUBLISH_TIME]: defaultTimeShown.format('LT'),
    };
    return initForm;
};
